/**
 * T0704 — Global "I have an idea" entry point.
 *
 * Available from the top bar on every page. If the user is already on an
 * entity page, that entity is pre-selected; otherwise the user picks an
 * entity first, then describes the idea.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lightbulb, Building2, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { QuickProcessPanel } from "./QuickProcessPanel";
import { listEntities, type EntityData } from "@/services/api/entityApi";
import { Confirm } from "@/components/admin/confirm";
import { getCurrentEntityId } from "@/providers/firebase/authProvider";

interface GlobalIdeaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalIdeaModal({ open, onOpenChange }: GlobalIdeaModalProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [entities, setEntities] = useState<EntityData[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [panelDirty, setPanelDirty] = useState(false);
  const [panelLoading, setPanelLoading] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  // Pre-select entity when the modal opens and the user is on /entities/:id.
  useEffect(() => {
    if (!open) {
      setSelectedEntityId(null);
      return;
    }

    const match = location.pathname.match(/^\/entities\/([^/]+)$/);
    const pageEntityId = match?.[1] ?? null;

    setEntitiesLoading(true);
    listEntities()
      .then((list) => {
        setEntities(list);
        if (pageEntityId && list.some((e) => e.entity_id === pageEntityId)) {
          setSelectedEntityId(pageEntityId);
        } else if (list.some((e) => e.entity_id === getCurrentEntityId())) {
          setSelectedEntityId(getCurrentEntityId());
        } else if (list.length === 1) {
          setSelectedEntityId(list[0].entity_id);
        } else {
          setSelectedEntityId(null);
        }
      })
      .catch(() => {
        toast.error("Could not load entities");
      })
      .finally(() => setEntitiesLoading(false));
  }, [open, location.pathname]);

  const selectedEntity = useMemo(
    () => entities.find((e) => e.entity_id === selectedEntityId),
    [entities, selectedEntityId],
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && (panelDirty || panelLoading)) {
      setConfirmClose(true);
      return;
    }
    onOpenChange(nextOpen);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-xl"
          onPointerDownOutside={(e) => {
            if (panelLoading) {
              e.preventDefault();
              toast.info("Raisy is still building your process", {
                description: "Please wait or close this dialog with the X button.",
              });
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              I have an idea
            </DialogTitle>
            <DialogDescription>
              {selectedEntity
                ? `Tell Raisy what ${selectedEntity.entity_name} should do next.`
                : "Choose which entity this idea is for."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Select
                value={selectedEntityId ?? ""}
                onValueChange={setSelectedEntityId}
                disabled={entitiesLoading || panelLoading}
              >
                <SelectTrigger className="flex-1 h-9 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <SelectValue placeholder="Select an entity…" />
                </SelectTrigger>
                <SelectContent>
                  {entitiesLoading ? (
                    <SelectItem value="__loading__" disabled>
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading entities…
                      </span>
                    </SelectItem>
                  ) : entities.length === 0 ? (
                    <SelectItem value="__empty__" disabled>
                      No entities found
                    </SelectItem>
                  ) : (
                    entities.map((e) => (
                      <SelectItem key={e.entity_id} value={e.entity_id}>
                        {e.entity_name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>

              {selectedEntityId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedEntityId(null)}
                  disabled={panelLoading}
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Change</span>
                </Button>
              )}
            </div>

            {selectedEntityId && (
              <QuickProcessPanel
                entityId={selectedEntityId}
                entityName={selectedEntity?.entity_name}
                onProcessCreated={(processId) => {
                  onOpenChange(false);
                  navigate(`/process-manager?process_id=${processId}`);
                }}
                onDirtyChange={setPanelDirty}
                onLoadingChange={setPanelLoading}
              />
            )}
            {panelLoading && (
              <p className="text-label-2xs text-muted-foreground">
                Tip: you can close this window. The process keeps building in the background
                and appears in the floating indicator at the bottom-right.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Confirm
        isOpen={confirmClose}
        title="Discard your idea?"
        content={
          panelLoading
            ? "Raisy is still building your process. You can close this window — construction continues in the background and you can follow it in the floating indicator."
            : "You have started an idea. Closing now will lose what you typed."
        }
        cancel={panelLoading ? "Keep waiting" : "Keep editing"}
        confirm="Discard"
        cancelVariant="default"
        confirmVariant="ghost"
        onClose={() => setConfirmClose(false)}
        onConfirm={() => {
          setConfirmClose(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
